import { clamp } from '../core/math';
import { applyWater, ignitionThreshold } from './fire';
import {
  CellState,
  ENGINE_DIRECT_LIMIT,
  FLAME_NAME,
  Flame,
  GRID_W,
  Road,
  T,
  TERRAIN,
  flameClass,
  flameMetres,
  idx,
  inBounds,
} from './types';
import type { Difficulty } from './difficulty';
import type { Village } from './terrain';
import type { World } from './world';

// ---------------------------------------------------------------------------
// Orders
// ---------------------------------------------------------------------------

export type Tactic = 'direct' | 'line' | 'protect' | 'road' | 'withdraw';

export const TACTICS: readonly { key: Tactic; name: string; hint: string }[] = [
  { key: 'direct', name: 'Direct attack', hint: 'Put water straight onto the burning edge. Refused where the flames are too big to stand next to.' },
  { key: 'line', name: 'Hold a line', hint: 'Wet a strip of ground along a road or an acequia (an old stone irrigation channel) and let the fire burn up to it.' },
  { key: 'protect', name: 'Protect', hint: 'Wet the buildings and the ground around them.' },
  { key: 'road', name: 'Keep road open', hint: 'Wet the road people are driving out on, and the ground beside it, so fire cannot close it.' },
  { key: 'withdraw', name: 'Withdraw', hint: 'Drive back down the road to the safe parking area in the valley.' },
];

export interface Objective {
  kind: 'village' | 'road' | 'fire';
  label: string;
  /** Cell the crew are working towards. */
  x: number;
  y: number;
  /** Where on the road they park to do it. */
  roadId: number;
  s: number;
  villageId: number;
}

export interface Order {
  tactic: Tactic;
  objective: Objective;
  /** The commander has explicitly told them to stay despite a warning. */
  hold: boolean;
}

// ---------------------------------------------------------------------------
// The unit
// ---------------------------------------------------------------------------

export enum EState {
  /** Still driving up from Órgiva or Vélez, so not on the map yet. */
  Responding = 0,
  /** On the map with no orders, parked at the valley end. */
  Staging = 1,
  Driving = 2,
  Working = 3,
  Resupply = 4,
  /** Pulling back because fire is about to cut the road out. */
  Withdrawing = 5,
  Lost = 6,
}

export interface Engine {
  id: number;
  name: string;
  roadId: number;
  /** Position along the road, in cells. */
  s: number;
  v: number;
  targetS: number;
  water: number;
  /** 1 = fresh crew, 0 = worn out. */
  crew: number;
  state: EState;
  order: Order | null;
  /** Seconds until it arrives on the map. */
  eta: number;
  /** Seconds spent refilling. */
  refill: number;
  /** True once the escape-route warning has been given for the current threat. */
  warned: boolean;
  /** Seconds since the warning; they pull out on their own when this runs out. */
  warnTimer: number;
  /** Set when the safety rules have overridden the tactic you ordered. */
  improvising: boolean;
  /** True while the crew are worn out, so the radio call happens once. */
  spent: boolean;
  /** Road they are driving round the valley to reach, or -1. */
  transferTo: number;
  /**
   * A standing one-line explanation of what this crew is doing and why, shown
   * in the dashboard. If the player cannot see the reason for what a crew
   * does, it looks like a bug, however correct it is.
   */
  note: string;
}

/**
 * How far a crew can work from the engine, per tactic. A quick attack straight
 * off the back of the truck is short. Protecting buildings means running hose
 * up to them, which is a couple of hundred metres in practice.
 */
const REACH: Record<Tactic, number> = {
  direct: 4.2,
  line: 5,
  protect: 8,
  road: 5,
  withdraw: 0,
};
const HOSE_REACH = 8;
const DRIVE_SPEED = 3.2;
/**
 * Where an engine with no orders waits. Parking on the map edge itself put the
 * sprite half off-screen, and stacked two of them in the same spot, so the
 * engines were effectively invisible until you gave them somewhere to go.
 */
const STAGE_IN = 6;
const STAGE_GAP = 5;
export const stagePoint = (road: Road, ordinal = 0): number =>
  Math.max(2, road.cells.length - 1 - STAGE_IN - ordinal * STAGE_GAP);
/** Slowest an engine will take a bend, whatever the limit for civilian cars. */
const EMERGENCY_MIN = 1.9;
/** Moisture added per second at the centre of the water jet, at full crew strength. */
const ENGINE_FLOW = 0.45;
/** Cells of fire-free road a crew needs between them and the valley. */
const ESCAPE_MARGIN = 14;
/** How close the fire may come to the crew themselves before they say so on the radio. */
const LOOKOUT_MARGIN = 11;
/** Closer than this and they leave now, without waiting for the countdown. */
const IMMINENT_MARGIN = 6;
/** Seconds between the warning and the crew leaving of their own accord. */
const WARN_GRACE = 18;

/** Pure factory: takes what it needs rather than a half-built World. */
export function makeEngines(diff: Difficulty, roads: readonly Road[]): Engine[] {
  const out: Engine[] = [];
  const onRoad = [0, 0];
  for (let i = 0; i < diff.engines; i++) {
    // Alternate which road they come up, so crews can reach both sides of the fire.
    const roadId = i % 2;
    const road = roads[roadId];
    // Spaced out along the staging area so two never park in the same spot.
    const park = stagePoint(road, onRoad[roadId]++);
    out.push({
      id: i,
      name: `Autobomba ${i + 1}`,
      roadId,
      s: park,
      v: 0,
      targetS: park,
      water: 1,
      crew: 1,
      state: EState.Responding,
      order: null,
      eta: diff.engineFirst + i * diff.engineStagger,
      refill: 0,
      warned: false,
      warnTimer: 0,
      improvising: false,
      spent: false,
      transferTo: -1,
      note: '',
    });
  }
  return out;
}

export const engineCell = (w: World, e: Engine): { x: number; y: number } => {
  const road = w.roads[e.roadId];
  return road.cells[clamp(Math.round(e.s), 0, road.cells.length - 1)];
};

export const isDeployed = (e: Engine): boolean =>
  e.state !== EState.Responding && e.state !== EState.Lost;

// ---------------------------------------------------------------------------
// Turning a click into an objective
// ---------------------------------------------------------------------------

/** Snap a map position to the nearest thing worth naming in an order. */
export function resolveObjective(w: World, cx: number, cy: number): Objective | null {
  let village: Village | null = null;
  let bestV = 7;
  for (const v of w.mtn.villages) {
    const d = Math.hypot(v.cx - cx, v.cy - cy);
    if (d < bestV) {
      bestV = d;
      village = v;
    }
  }

  // Nearest point of road, which is where an engine can actually get to.
  let roadId = -1;
  let bestS = -1;
  let bestD = Infinity;
  w.roads.forEach((road) => {
    road.cells.forEach((c, s) => {
      const d = Math.hypot(c.x - cx, c.y - cy);
      if (d < bestD) {
        bestD = d;
        bestS = s;
        roadId = road.id;
      }
    });
  });
  if (roadId < 0) return null;

  if (village) {
    return {
      kind: 'village',
      label: village.name,
      x: village.cx,
      y: village.cy,
      roadId: village.roadId,
      s: village.accessS,
      villageId: village.id,
    };
  }

  const onFire =
    inBounds(cx, cy) && w.grid.state[idx(cx, cy)] === CellState.Burning;
  const road = w.roads[roadId];
  return {
    kind: onFire ? 'fire' : 'road',
    label: onFire ? `the fire above ${road.name.split('·')[1]?.trim() ?? road.name}` : road.name,
    x: road.cells[bestS].x,
    y: road.cells[bestS].y,
    roadId,
    s: bestS,
    villageId: -1,
  };
}

/**
 * Every objective worth naming, for the control dashboard. Picking from a list
 * is better than clicking the map: it needs no aiming, it works while paused,
 * and it shows everything you could be defending in one place.
 */
export function namedObjectives(w: World): { key: string; label: string; obj: Objective }[] {
  const out: { key: string; label: string; obj: Objective }[] = [];

  for (const v of w.mtn.villages) {
    out.push({
      key: `v${v.id}`,
      label: v.name,
      obj: {
        kind: 'village',
        label: v.name,
        x: v.cx,
        y: v.cy,
        roadId: v.roadId,
        s: v.accessS,
        villageId: v.id,
      },
    });
  }

  for (const road of w.roads) {
    const short = road.name.split('·')[1]?.trim() ?? road.name;
    const marks: [string, number][] = [
      ['top', 0.12],
      ['middle', 0.45],
      ['bottom', 0.8],
    ];
    for (const [where, f] of marks) {
      const s = Math.round((road.cells.length - 1) * f);
      const c = road.cells[s];
      out.push({
        key: `r${road.id}${where}`,
        label: `${short} road, ${where}`,
        obj: { kind: 'road', label: `${short} road (${where})`, x: c.x, y: c.y, roadId: road.id, s, villageId: -1 },
      });
    }
  }

  // Wherever the fire is closest to a road a crew could work from.
  let best: Objective | null = null;
  let bestD = Infinity;
  for (const road of w.roads) {
    road.cells.forEach((c, s) => {
      const d = w.fireDistance(c.x, c.y);
      if (d < bestD) {
        bestD = d;
        best = { kind: 'fire', label: 'the fire edge', x: c.x, y: c.y, roadId: road.id, s, villageId: -1 };
      }
    });
  }
  if (best && bestD < 14) out.push({ key: 'fire', label: `The fire edge (${Math.round(bestD)} cells off the road)`, obj: best });

  return out;
}

/** Why a tactic is unavailable against this objective, or null if it is fine. */
export function tacticRefusal(w: World, tactic: Tactic, obj: Objective): string | null {
  if (tactic === 'withdraw') return null;
  if (tactic === 'protect' && obj.kind !== 'village') return 'Nothing to protect there. Pick a village.';
  if (tactic === 'road' && obj.kind === 'village') return null;
  if (tactic === 'direct') {
    const worst = hottestNear(w, obj.x, obj.y, 6);
    if (worst === null) return 'No fire within reach of that point.';
    if (worst > ENGINE_DIRECT_LIMIT)
      return `Flame length about ${flameMetres(worst === Flame.Extreme ? 0.9 : 0.65).toFixed(1)} m. Crews cannot work next to flames that big.`;
  }
  return null;
}

function hottestNear(w: World, cx: number, cy: number, radius: number): Flame | null {
  let worst: Flame | null = null;
  const r = Math.ceil(radius);
  for (let y = cy - r; y <= cy + r; y++) {
    for (let x = cx - r; x <= cx + r; x++) {
      if (!inBounds(x, y)) continue;
      const i = idx(x, y);
      if (w.grid.state[i] !== CellState.Burning) continue;
      const f = flameClass(w.grid.inten[i]);
      if (worst === null || f > worst) worst = f;
    }
  }
  return worst;
}

export function issueOrder(w: World, e: Engine, tactic: Tactic, obj: Objective): boolean {
  if (e.state === EState.Lost || e.state === EState.Responding) return false;

  // Giving the same order again while a warning is running means "hold".
  if (e.warned && e.order && e.order.tactic === tactic && e.order.objective.label === obj.label) {
    e.order.hold = true;
    e.warnTimer = Infinity;
    w.say(`${e.name} holding position on your order. Watch their road.`, 'warn');
    return true;
  }

  const refusal = tacticRefusal(w, tactic, obj);
  if (refusal) {
    w.say(`${e.name}: ${refusal}`, 'warn');
    return false;
  }

  if (tactic === 'withdraw') {
    e.order = null;
    e.state = EState.Withdrawing;
    w.say(`${e.name} pulling back to the valley.`, 'info');
    return true;
  }

  // A line has to start from something that will not burn, or the fire gets round the end of it.
  const anchored = anchorFor(w, obj);
  e.order = { tactic, objective: { ...obj, s: anchored }, hold: false };

  if (obj.roadId !== e.roadId) {
    // Both roads are the A-348: they meet down in the valley. The crew drive
    // out of one end and back up the other, which costs real time.
    e.transferTo = obj.roadId;
    e.targetS = w.roads[e.roadId].cells.length - 1;
    w.say(`${e.name} driving round through the valley to reach ${obj.label}.`, 'warn');
  } else {
    e.transferTo = -1;
    e.targetS = anchored;
  }
  e.state = EState.Driving;
  // Deliberately does NOT clear the escape-route warning. The danger belongs to
  // the situation, not to the order. Otherwise giving a crew a new job every
  // few seconds would keep resetting their withdrawal timer and get them killed.
  // Only lcesCheck clears it, when the road is genuinely safe again.
  e.improvising = false;
  e.note = '';
  const verb =
    tactic === 'direct'
      ? 'attacking the flames at'
      : tactic === 'line'
        ? 'starting a wet line at'
        : tactic === 'protect'
          ? 'protecting'
          : 'holding the road at';
  w.say(`${e.name} ${verb} ${obj.label}.`, 'info');
  if (anchored !== obj.s) {
    w.say(`${e.name}: nothing there that will not burn, so the line starts from the road instead.`, 'warn');
  }
  return true;
}

/**
 * A line has to start from something that will not burn, which is called an
 * anchor point. Roads do not burn, so this mostly matters when the spot the
 * player asked for is already in the path of the fire.
 */
function anchorFor(w: World, obj: Objective): number {
  const road = w.roads[obj.roadId];
  const n = road.cells.length;
  let s = clamp(obj.s, 0, n - 1);
  // Never park where the road is already blocked; move downhill instead.
  let guard = 0;
  while (guard++ < n && Number.isFinite(road.blocked[s]) && road.blocked[s] <= s + 0.5) s++;
  return clamp(s, 0, n - 1);
}

// ---------------------------------------------------------------------------
// Doing the job
// ---------------------------------------------------------------------------

export function stepEngines(w: World, dt: number): void {
  for (const e of w.engines) {
    if (e.state === EState.Lost) continue;

    if (e.state === EState.Responding) {
      e.eta -= dt;
      if (e.eta <= 0) {
        e.eta = 0;
        e.state = EState.Staging;
        w.say(`${e.name} on scene at the ${w.roads[e.roadId].name}, awaiting orders.`, 'good');
        // The first one has to teach the player how to give orders, or a player
        // who has only ever flown the helicopter will never find them.
        if (!w.flags.ordersExplained) {
          w.flags.ordersExplained = true;
          w.say(
            'Each Autobomba is a fire engine. Click a unit in GROUND UNITS, pick a tactic, then click a village, a road or the fire.',
            'info',
          );
        }
      }
      continue;
    }

    if (burnoverCheck(w, e, dt)) continue;

    lcesCheck(w, e, dt);
    drive(w, e, dt);

    switch (e.state) {
      case EState.Working:
        work(w, e, dt);
        break;
      case EState.Resupply:
        if (Math.abs(e.s - e.targetS) < 1) {
          e.refill += dt;
          e.crew = Math.min(1, e.crew + dt / (w.diff.crewEndurance * 0.55));
          if (e.refill >= w.diff.refillTime) {
            e.refill = 0;
            e.water = 1;
            w.say(`${e.name} has a full tank and is heading back to the fire.`, 'good');
            if (e.order) {
              e.targetS = e.order.objective.s;
              e.state = EState.Driving;
            } else {
              e.state = EState.Staging;
            }
          }
        }
        break;
      default:
        // Not working the fire: the crew rest, and a water tanker at the
        // staging area refills the tank. Without this an engine that was
        // pushed off the hill on its way to water sat there dry for the rest
        // of the incident, out of the game, with no way for you to fix it.
        e.crew = Math.min(1, e.crew + dt / (w.diff.crewEndurance * 0.8));
        if (e.state === EState.Staging && e.water < 1) {
          e.water = Math.min(1, e.water + dt / (w.diff.refillTime * 1.5));
          if (e.water >= 1 && !e.spent) w.say(`${e.name} is refuelled and watered, ready to go again.`, 'good');
        }
        break;
    }
  }
}

function drive(w: World, e: Engine, dt: number): void {
  if (e.state === EState.Working) return;
  const road = w.roads[e.roadId];
  const n = road.cells.length;

  if (e.state === EState.Withdrawing) e.targetS = stagePoint(road);

  const gap = e.targetS - e.s;
  if (Math.abs(gap) < 0.6) {
    e.v = 0;
    e.s = e.targetS;

    // Reached the valley end on the way round to the other road.
    if (e.transferTo >= 0 && e.state === EState.Driving) {
      const to = w.roads[e.transferTo];
      e.roadId = e.transferTo;
      e.transferTo = -1;
      e.s = to.cells.length - 1;
      e.targetS = e.order ? e.order.objective.s : e.s;
      return;
    }

    if (e.state === EState.Driving) {
      e.state = EState.Working;
      e.improvising = false;
    } else if (e.state === EState.Withdrawing) {
      e.state = EState.Staging;
    }
    return;
  }

  const dir = Math.sign(gap);
  // Fire on the road stops an engine exactly as it stops anyone else.
  const here = clamp(Math.round(e.s), 0, n - 1);
  const block = dir > 0 ? road.blocked[here] : road.blockedBehind[here];
  if (Number.isFinite(block) && Math.abs(block - e.s) < 2.5) {
    e.v = 0;
    if (!e.improvising) {
      e.improvising = true;
      w.say(`${e.name} cannot get through. There is fire across the road ahead of them.`, 'warn');
    }
    return;
  }

  // A fire engine with its lights and siren on takes the bends faster than a
  // queue of frightened families, so an engine is not held to the hairpin
  // speed limit used for civilian cars.
  const limit =
    Math.min(DRIVE_SPEED, Math.max(road.cells[here].limit, EMERGENCY_MIN)) * (road.scorched[here] ? 0.6 : 1);
  e.v = limit;
  e.s = clamp(e.s + dir * limit * dt, 0, n - 1);
}

function work(w: World, e: Engine, dt: number): void {
  const { diff, grid } = w;
  if (!e.order) {
    e.state = EState.Staging;
    return;
  }
  if (e.water <= 0.001) {
    sendToWater(w, e);
    return;
  }

  const here = engineCell(w, e);
  const effectiveness = 0.35 + 0.65 * e.crew;
  const target = pickTarget(w, e, here.x, here.y);

  if (target < 0) {
    // Nothing to do here right now: rest rather than pour water away.
    e.crew = Math.min(1, e.crew + dt / (w.diff.crewEndurance * 1.4));
    return;
  }

  const tx = target % GRID_W;
  const ty = Math.floor(target / GRID_W);
  applyWater(grid, tx, ty, 1.4, ENGINE_FLOW * effectiveness * dt);

  e.water = Math.max(0, e.water - dt / diff.engineDrain);
  e.crew = Math.max(0, e.crew - (dt / diff.crewEndurance) * (1 + diff.ambientHeat));

  if (e.water <= 0.001) {
    sendToWater(w, e);
    return;
  }
  // Report exhaustion once per episode, not once per tick.
  if (e.crew < 0.18 && !e.spent) {
    e.spent = true;
    w.say(`${e.name}: crew are worn out and getting little done. Rest them or send another engine.`, 'warn');
  } else if (e.crew > 0.5) {
    e.spent = false;
  }
}

/**
 * What this crew works on, given their orders and what the safety rules allow.
 * If the ordered tactic is impossible they fall back to the safe one rather
 * than standing there doing nothing, and say so once.
 */
function pickTarget(w: World, e: Engine, ex: number, ey: number): number {
  const { grid } = w;
  const order = e.order!;
  const reach = REACH[order.tactic];
  const r = Math.ceil(reach);

  let best = -1;
  let bestScore = -Infinity;
  let sawTooHot = false;

  for (let y = ey - r; y <= ey + r; y++) {
    for (let x = ex - r; x <= ex + r; x++) {
      if (!inBounds(x, y)) continue;
      const d = Math.hypot(x - ex, y - ey);
      if (d > reach) continue;
      const i = idx(x, y);
      const def = TERRAIN[grid.terrain[i]];
      if (def.fuel <= 0) continue;

      let score = -Infinity;
      if (grid.state[i] === CellState.Burning) {
        const f = flameClass(grid.inten[i]);
        if (f > ENGINE_DIRECT_LIMIT) {
          sawTooHot = true;
          continue; // never put a crew on an edge that hot
        }
        // A direct attack aims at burning cells. The other tactics water them
        // too if they are right in front of the crew.
        score = (order.tactic === 'direct' ? 220 : 120) - d * 4;
      } else if (grid.state[i] === CellState.Unburnt) {
        const heat = grid.heat[i] / Math.max(0.05, ignitionThreshold(grid, i));
        if (grid.moist[i] > 0.55) continue; // already wet, do not waste the tank
        if (order.tactic === 'protect') {
          // Protecting a building means the ground around it as well: you wet
          // a margin the crew can defend, not just the walls.
          if (grid.terrain[i] !== T.Building && !within(grid, x, y, T.Building, 2)) continue;
          score = 150 + heat * 90 - d * 3;
        } else if (order.tactic === 'road') {
          if (!neighbourIs(grid, x, y, T.Road) && grid.terrain[i] !== T.Road) continue;
          score = 120 + heat * 120 - d * 3;
        } else if (order.tactic === 'line') {
          if (heat < 0.05) continue;
          score = 60 + heat * 160 - d * 3;
        } else {
          continue;
        }
      }

      if (score > bestScore) {
        bestScore = score;
        best = i;
      }
    }
  }

  if (best < 0 && sawTooHot && !e.improvising) {
    e.improvising = true;
    const worst = hottestNear(w, ex, ey, r);
    w.say(
      `${e.name}: flame is ${FLAME_NAME[worst ?? Flame.High]} at the head of the fire, the fast-moving front. We cannot work there, so we are attacking along the side of it and wetting the ground instead.`,
      'warn',
    );
  }
  return best;
}

const neighbourIs = (grid: World['grid'], x: number, y: number, t: T): boolean => within(grid, x, y, t, 1);

const within = (grid: World['grid'], x: number, y: number, t: T, r: number): boolean => {
  for (let dy = -r; dy <= r; dy++)
    for (let dx = -r; dx <= r; dx++) {
      if (!inBounds(x + dx, y + dy)) continue;
      if (grid.terrain[idx(x + dx, y + dy)] === t) return true;
    }
  return false;
};

function sendToWater(w: World, e: Engine): void {
  const points = w.mtn.waterPoints.filter((p) => p.roadId === e.roadId);
  if (points.length === 0) {
    e.state = EState.Withdrawing;
    w.say(`${e.name} is dry and has no water point on their road, so they are coming down.`, 'warn');
    return;
  }
  const road = w.roads[e.roadId];
  let best = points[0];
  let bestCost = Infinity;
  for (const p of points) {
    // Prefer close ones, and never send a crew through fire to get water.
    const between = Math.min(p.s, e.s);
    const blocked = Number.isFinite(road.blocked[clamp(Math.round(between), 0, road.cells.length - 1)]);
    const cost = Math.abs(p.s - e.s) + (blocked ? 500 : 0);
    if (cost < bestCost) {
      bestCost = cost;
      best = p;
    }
  }
  e.targetS = best.s;
  e.refill = 0;
  e.state = EState.Resupply;
  w.say(`${e.name} is dry, refilling from the ${best.kind} (a water point by the road) at ${Math.round(best.s)}.`, 'info');
  // Drive there first; the refill only starts on arrival.
  if (Math.abs(e.s - e.targetS) > 1) e.v = 0;
}

/**
 * LCES: Lookouts, Communications, Escape routes, Safety zones. The check that
 * pulls a crew out before the fire can trap them.
 */
function lcesCheck(w: World, e: Engine, dt: number): void {
  if (e.state === EState.Withdrawing || e.state === EState.Responding) return;
  // Not deployed yet: let them set off before anyone starts worrying.
  if (atStagingArea(w, e)) {
    e.warned = false;
    e.warnTimer = 0;
    return;
  }
  const road = w.roads[e.roadId];
  const here = clamp(Math.round(e.s), 0, road.cells.length - 1);
  const risk = road.riskAhead[here];

  // Escape routes: fire on the road between this crew and the valley.
  const routeThreatened = Number.isFinite(risk) && risk - e.s < ESCAPE_MARGIN;
  // Lookouts: fire closing on the crew themselves. Fire spreading sideways can
  // reach an engine without ever touching the road below it.
  const c = engineCell(w, e);
  const closeThreatened = w.fireDistance(c.x, c.y) < LOOKOUT_MARGIN;
  const threatened = routeThreatened || closeThreatened;

  if (!threatened) {
    if (e.warned) {
      e.warned = false;
      e.warnTimer = 0;
      if (e.order) e.order.hold = false;
    }
    return;
  }

  // Imminent danger overrides the grace period. The countdown exists so the
  // commander can judge a threat that is still developing. A crew about to be
  // caught by the fire leaves on the decision of their own crew boss, which is
  // exactly how it works for real. An explicit HOLD still keeps them there, and
  // that is what makes it your decision.
  const imminent = w.fireDistance(c.x, c.y) < IMMINENT_MARGIN;
  if (imminent && !e.order?.hold) {
    // Already returned above if they were withdrawing, so this is always a
    // crew that is still out working.
    e.note = `Pulled back from ${e.order?.objective.label ?? 'the line'} because the fire got too close`;
    e.state = EState.Withdrawing;
    e.order = null;
    e.warned = true;
    w.say(`${e.name} disengaging. The fire has reached them.`, 'bad');
    return;
  }

  if (!e.warned) {
    e.warned = true;
    e.warnTimer = WARN_GRACE;
    w.say(
      routeThreatened
        ? `${e.name}: fire is moving towards our escape route. Pulling out in ${WARN_GRACE}s unless you order us to hold.`
        : `${e.name}: fire closing on our position. Pulling out in ${WARN_GRACE}s unless you order us to hold.`,
      'bad',
    );
    return;
  }

  if (e.order?.hold) return;
  e.warnTimer -= dt;
  if (e.warnTimer <= 0) {
    e.note = `Withdrew from ${e.order?.objective.label ?? 'the line'} because fire was closing on the road out`;
    e.state = EState.Withdrawing;
    e.order = null;
    w.say(`${e.name} withdrawing. The fire was about to cut off their way out.`, 'warn');
  }
}

/**
 * Still down at the staging area in the valley. That is the incident command
 * post, the spot the units are run from, and it was chosen because it is safe.
 * A crew here has not set off yet, whether or not they have just been handed
 * orders. Without this an order given while fire was anywhere near the road
 * exit was cancelled by the safety check before the engine had moved a single
 * cell, which looks exactly like a bug.
 */
const atStagingArea = (w: World, e: Engine): boolean => e.s >= stagePoint(w.roads[e.roadId], 3);

/** Returns true if the crew were lost. */
function burnoverCheck(w: World, e: Engine, dt: number): boolean {
  if (atStagingArea(w, e)) return false;

  const c = engineCell(w, e);
  let heat = 0;
  for (let dy = -1; dy <= 1; dy++)
    for (let dx = -1; dx <= 1; dx++) {
      if (!inBounds(c.x + dx, c.y + dy)) continue;
      const i = idx(c.x + dx, c.y + dy);
      if (w.grid.state[i] === CellState.Burning) heat += w.grid.inten[i];
    }
  if (heat < 1.2) return false;

  // A burnover, where the fire runs over the crew, is survivable for a few
  // seconds: fire shelters, the cab and the water tank buy that much. It is not
  // survivable for much longer, and a worn-out crew has less in reserve.
  e.crew = Math.max(0, e.crew - dt * 0.28);
  if (heat > 3.2 || e.crew <= 0) {
    e.state = EState.Lost;
    e.order = null;
    w.stats.crewLost++;
    w.stats.enginesLost++;
    w.say(`The fire ran over ${e.name}. Crew lost.`, 'bad');
    return true;
  }
  return false;
}

/** Cells within hose reach of a parked engine, drawn when it is selected. */
export const engineReach = (): number => HOSE_REACH;

/** One-line status for the list of units. */
export function engineStatus(w: World, e: Engine): string {
  switch (e.state) {
    case EState.Responding:
      return `responding · ${Math.ceil(e.eta)}s`;
    case EState.Staging:
      return 'AWAITING ORDERS';
    case EState.Driving:
      return e.order ? `to ${e.order.objective.label}` : 'moving';
    case EState.Working: {
      const t = e.order ? TACTICS.find((x) => x.key === e.order!.tactic)?.name ?? '' : '';
      return `${t} · ${e.order?.objective.label ?? ''}`;
    }
    case EState.Resupply:
      return Math.abs(e.s - e.targetS) < 1 ? `refilling · ${Math.ceil(w.diff.refillTime - e.refill)}s` : 'going for water';
    case EState.Withdrawing:
      return 'withdrawing';
    case EState.Lost:
      return 'LOST';
  }
}

export { HOSE_REACH };


